import * as Sentry from '@sentry/browser'
import { isApiClientError } from './ApiResponseError'
import { observeSlowApiCalls } from '../metrics/observeSlowApiCalls'

export const DEFAULT_REPLAY_PATH_PATTERN = ''

/**
 * Share of non-error sessions to record on replay pages, from 0 to 1.
 * Anything missing or invalid falls back to 0 (errors only).
 * @returns {number}
 */
export const getReplaySessionSampleRate = () => {
  // @ts-ignore
  const rate = parseFloat(
    import.meta.env.VITE_REPLAY_NO_ERROR_SESSION_SAMPLE_RATE,
  )

  if (Number.isNaN(rate)) {
    return 0
  }

  return Math.min(Math.max(rate, 0), 1)
}

/**
 * Whether this page should record a replay. The pattern is tested against the
 * path plus query string, so it can target e.g. `[?&]flow=foo(&|$)`.
 * @returns {boolean}
 */
export const shouldRecordReplay = () => {
  // @ts-ignore
  const pattern =
    import.meta.env.VITE_REPLAY_PATH_PATTERN || DEFAULT_REPLAY_PATH_PATTERN

  if (!pattern) {
    return false
  }

  let matcher
  try {
    matcher = new RegExp(pattern, 'i')
  } catch {
    return false
  }

  return matcher.test(window.location.pathname + window.location.search)
}

/**
 * First-party API hosts whose 5xx responses are reported to Sentry, plus the
 * current origin (brand domains call same-origin /api/* routes). Third-party
 * offer and tracking hosts are left out to keep noise down.
 * @returns {RegExp[]}
 */
export const getFailedRequestTargets = () => [
  /^https:\/\/(?:.+\.)?trbotrkr\.com\//,
  /^https:\/\/(?:.+\.)?turbodebt\.com\//,
  /^https:\/\/(?:.+\.)?turbodevhub\.com\//,
  new RegExp(
    `^${window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/`,
  ),
]

/**
 * Hosts that get sentry-trace/baggage headers so their traces link up. Keep it
 * an explicit list: once Sentry starts early, a matched cross-origin host whose
 * CORS preflight doesn't allow those headers breaks the request outright (that
 * is how blog.turbodevhub.com broke the /thanks offers fetch). Every host here
 * was checked to allow them.
 */
export const TRACE_PROPAGATION_TARGETS = [
  'localhost',
  /^https:\/\/trbotrkr\.com\//,
  /^https:\/\/(?:middleware-staging|module-manager)\.turbodevhub\.com\//,
]

/**
 * How long after `load` the pageload transaction stays open, so the requests
 * pages fire right after load (GTM, form-loaded metrics…) still land in it
 */
export const PAGELOAD_SETTLE_MS = 2000

/**
 * Ends the pageload transaction PAGELOAD_SETTLE_MS after the page has loaded
 */
const reportPageLoadedAfterLoad = () => {
  const report = () =>
    setTimeout(() => Sentry.reportPageLoaded?.(), PAGELOAD_SETTLE_MS)

  if (document.readyState === 'complete') {
    report()
  } else {
    window.addEventListener('load', report, { once: true })
  }
}

/**
 * Load Sentry. Call it as early as possible: the pageload transaction stays
 * open until shortly after `load` either way.
 * @param {string} dsn - Sentry DSN
 * @param {Partial<import('@sentry/browser').BrowserOptions>} [overrides] - Optional Sentry.init overrides
 */
export const loadSentry = (dsn, overrides = {}) => {
  try {
    const recordReplay = shouldRecordReplay()

    Sentry.init({
      dsn,
      enableLogs: true,
      integrations: [
        Sentry.browserTracingIntegration({ enableReportPageLoaded: true }),
        // only form field values stay masked; page text and media are shown
        ...(recordReplay
          ? [
              Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: true,
                blockAllMedia: false,
              }),
            ]
          : []),
        Sentry.captureConsoleIntegration({ levels: ['error'] }),
        Sentry.httpClientIntegration({
          failedRequestStatusCodes: [[500, 599]],
          failedRequestTargets: getFailedRequestTargets(),
        }),
        // @ts-ignore
        ...(import.meta.env.DEV ? [Sentry.spotlightBrowserIntegration()] : []),
      ],
      beforeSend: (event, hint) => {
        // Avoid re-reporting logged 4xx as errors
        if (isApiClientError(hint?.originalException)) {
          return null
        }
        if (
          // @ts-ignore
          typeof window.disableSentry === 'function' &&
          // @ts-ignore
          window.disableSentry() === true
        ) {
          return null
        }
        return event
      },
      // Tracing
      // @ts-ignore
      tracesSampleRate: import.meta.env.DEV ? 1 : 0.5,
      tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
      replaysSessionSampleRate: getReplaySessionSampleRate(),
      replaysOnErrorSampleRate: 1.0,
      ...overrides,
    })

    // grab the user trace to pass to HotJar
    const sentryUserTrace = Sentry.withScope((scope) => {
      const userTraceId = scope.getScopeData().propagationContext.traceId
      return userTraceId || null
    })

    // @ts-ignore
    window.sentryUserTraceId = sentryUserTrace

    reportPageLoadedAfterLoad()
    observeSlowApiCalls()
  } catch (error) {
    console.warn('Sentry failed to initialize', error)
  }
}
